import { Code, ConnectError } from '@connectrpc/connect'
import { isNetworkError } from 'utils/js'
import type { MessageFailureLike } from './message-failure-error'
import { connectErrorFromMessageFailure } from './message-failure-error'
import { restoresDraftOnSendFailure, toAidaChatError } from './errors'

export type FriendlyFailure = 'network' | 'timeout' | 'model'

const clientFailures = new Map<string, FriendlyFailure>()

export function rememberFriendlyFailure(messageId: string, kind: FriendlyFailure) {
  clientFailures.set(messageId, kind)
}

export function friendlyFailureFor(messageId: string): FriendlyFailure | undefined {
  return clientFailures.get(messageId)
}

export function friendlyFailureFromUnknown(error: unknown): FriendlyFailure {
  if (isNetworkError(error)) {
    return 'network'
  }
  if (!(error instanceof ConnectError)) {
    return 'model'
  }
  switch (error.code) {
    case Code.DeadlineExceeded: {
      return 'timeout'
    }
    case Code.Unavailable: {
      return 'network'
    }
    default: {
      return 'model'
    }
  }
}

export function friendlyFailureFromMessageFailure(
  failure: MessageFailureLike | undefined,
): FriendlyFailure {
  const connectError = failure ? connectErrorFromMessageFailure(failure) : null
  if (!connectError) {
    return 'model'
  }
  return friendlyFailureFromUnknown(connectError)
}

export function isInThreadRetryableSendError(error: unknown) {
  return !restoresDraftOnSendFailure(toAidaChatError(error))
}

export function sendFailureCopy(kind: FriendlyFailure) {
  switch (kind) {
    case 'network': {
      return {
        title: "Couldn't send that.",
        description: 'Check your connection and try again.',
      }
    }
    case 'timeout': {
      return {
        title: 'That took too long.',
        description: 'Try again in a moment.',
      }
    }
    case 'model': {
      return {
        title: "Aida couldn't reply.",
        description: 'Try again.',
      }
    }
  }
}

export function generationFailureCopy(kind: FriendlyFailure, detail?: string) {
  if (detail?.trim()) {
    return {
      title: 'Generation failed.',
      description: detail.trim(),
    }
  }
  switch (kind) {
    case 'network': {
      return {
        title: 'Generation failed.',
        description: 'Check your connection and try again.',
      }
    }
    case 'timeout': {
      return {
        title: 'Generation failed.',
        description: 'That took too long. Try again.',
      }
    }
    case 'model': {
      return {
        title: 'Generation failed.',
        description: 'Try again in a moment.',
      }
    }
  }
}
